import React from "react";
import { Button } from "@/components/ui/button";
import { Delete, X } from "lucide-react";

interface NumericKeyboardButton {
  key: string;
  type: "number" | "action";
  label?: string | React.ReactNode;
  className?: string;
}

interface NumericKeyboardProps {
  value: string;
  onChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  translator: (key: string) => string;
  title?: string;
  allowLeadingZeroes?: boolean;
}

const NumericKeyboard: React.FC<NumericKeyboardProps> = ({
  value,
  onChange,
  onConfirm,
  onCancel,
  translator,
  title = "Amount",
  allowLeadingZeroes = false,
}) => {
  const handleNumberClick = (num: string) => {
    if (num === "." && value.includes(".")) return;
    if (num !== "." && value.includes(".") && value.length - value.indexOf(".") > 2) return;
    if (!allowLeadingZeroes && (value === "0" || value === "-0") && num !== ".") {
      onChange(value.startsWith("-") ? `-${num}` : num);
    } else {
      onChange(value + num);
    }
  };

  const handleDelete = () => {
    const displayed = (value || "0").replace(/\.0+$/, "") || "0";
    if (displayed === "0" || displayed === "-0") return;
    const newValue = displayed.slice(0, -1) || "0";
    onChange(newValue);
  };

  const keypadLayout: NumericKeyboardButton[][] = [
    [
      { key: "7", type: "number" },
      { key: "8", type: "number" },
      { key: "9", type: "number" },
    ],
    [
      { key: "4", type: "number" },
      { key: "5", type: "number" },
      { key: "6", type: "number" },
    ],
    [
      { key: "1", type: "number" },
      { key: "2", type: "number" },
      { key: "3", type: "number" },
    ],
    [
      { key: "-", type: "number", label: "-" },
      { key: "0", type: "number" },
      { key: ".", type: "number", label: "•" },
      {
        key: "delete",
        type: "action",
        label: <Delete className="w-5 h-5" />,
        className: "bg-red-500 hover:bg-red-600 text-white",
      },
    ],
  ];

  const handleButtonClick = (buttonKey: string, e: React.MouseEvent) => {
    e.stopPropagation();

    switch (buttonKey) {
      case "delete":
        handleDelete();
        break;
      case "confirm":
        onConfirm();
        break;
      default:
        handleNumberClick(buttonKey);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background border border-border w-full max-w-2xl mx-auto rounded-lg shadow-lg">

      <div className="flex items-center justify-center p-4 pl-24 bg-muted border-b border-border">
        <X className="cursor-pointer absolute right-3" onClick={(e) => {
          e.stopPropagation();
          onCancel();
        }} />
        <h3 className="text-lg font-semibold text-foreground">
          {title}
        </h3>
        <div className="w-20"></div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="text-center">
          <div className="text-5xl font-mono font-bold text-foreground mb-2 border-2 ">
            {(value || "0").replace(/\.0+$/, "") }
          </div>
        </div>
      </div>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onConfirm();
        }}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg ml-auto mr-5 text-xl "
      >
        {translator("Tabs.accept")}
      </Button>

      <div className="bg-background p-6">
        <div className="grid grid-cols-12 gap-3 w-full max-w-md mx-auto">
          {keypadLayout.flat().map((button, index) => {
            const isLastRow = index >= 9;
            const baseClassName =
              "h-14 text-xl font-semibold border-0 rounded-lg";
            const numberClassName =
              "bg-secondary hover:bg-secondary/80 text-secondary-foreground";
            const className =
              button.type === "number"
                ? `${baseClassName} ${numberClassName}`
                : `${baseClassName} ${button.className || ""}`;
            return (
              <Button
                key={`${button.key}-${index}`}
                className={`${className} ${isLastRow ? "col-span-3" : "col-span-4"}`}
                onClick={(e) => handleButtonClick(button.key, e)}
              >
                {button.label || button.key}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NumericKeyboard;
